import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { specLabel } from "@/lib/specialisms";
import { toast } from "sonner";
import { Briefcase, Calendar, CheckCircle2, Clock, Film, Lock, PartyPopper, PoundSterling, XCircle } from "lucide-react";

const STATUS_BADGE = {
    applied: { label: "Applied — in review", cls: "bg-yc-cream", Icon: Clock },
    approved: { label: "You got the job", cls: "bg-yc-blue text-white", Icon: CheckCircle2 },
    rejected: { label: "Not selected", cls: "bg-yc-ink text-white", Icon: XCircle },
};

const CreatorJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [applying, setApplying] = useState("");
    const [applyFor, setApplyFor] = useState(""); // job_id with the open apply form
    const [links, setLinks] = useState({ work: ["", "", ""], proposal: ["", "", ""] });
    const [profileLinks, setProfileLinks] = useState([]);
    const [welcome, setWelcome] = useState(null); // application needing the welcome popup

    const load = async () => {
        const r = await api.get("/creator/jobs");
        setJobs(r.data);
        setLoaded(true);
        const pending = r.data.find((j) => j.application_status === "approved" && !j.welcome_seen);
        if (pending) setWelcome(pending);
    };
    useEffect(() => {
        load();
        api.get("/creator/profile").then((r) => {
            const d = r.data || {};
            const links = [d.social_links, d.portfolio_link, d.showreel_link, ...(d.instagram_video_links || [])].filter(Boolean);
            setProfileLinks(links);
        }).catch(() => {});
    }, []);

    const openApply = (job) => {
        setApplyFor(job.job_id);
        // Prefill "already done" with the creator's existing work links
        setLinks({
            work: [0, 1, 2].map((i) => profileLinks[i] || ""),
            proposal: ["", "", ""],
        });
    };

    const setLink = (kind, i, v) => {
        setLinks((prev) => {
            const next = [...prev[kind]];
            next[i] = v;
            return { ...prev, [kind]: next };
        });
    };

    const apply = async (job) => {
        const clean = (arr) => arr.map((l) => l.trim()).filter(Boolean);
        const bad = [...clean(links.work), ...clean(links.proposal)].find((l) => !/^https?:\/\//.test(l));
        if (bad) { toast.error("Links must start with https://"); return; }
        setApplying(job.job_id);
        try {
            await api.post(`/creator/jobs/${job.job_id}/apply`, {
                work_links: clean(links.work),
                proposal_links: clean(links.proposal),
            });
            toast.success("Application sent — the team will review it shortly");
            setApplyFor("");
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : (detail || "Could not apply"));
        } finally { setApplying(""); }
    };

    const dismissWelcome = async () => {
        if (welcome?.application_id) {
            try { await api.post(`/creator/applications/${welcome.application_id}/welcome-seen`); } catch { /* non-blocking */ }
        }
        setWelcome(null);
        load();
    };

    return (
        <DashboardShell role="creator" title="Jobs board" subtitle="Live briefs from the Curated Chaos team — one tap to apply">
            {/* Welcome aboard popup */}
            {welcome && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="welcome-popup">
                    <div className="absolute inset-0 bg-yc-ink/60" onClick={dismissWelcome} />
                    <div className="relative yc-card bg-white p-8 max-w-md w-full text-center">
                        <div className="w-14 h-14 mx-auto rounded-full bg-yc-pink text-white border-2 border-yc-ink flex items-center justify-center">
                            <PartyPopper className="w-7 h-7" />
                        </div>
                        <h2 className="font-heading text-2xl font-bold mt-4">Welcome aboard.</h2>
                        <p className="text-sm text-yc-ink/80 mt-2 leading-relaxed">
                            You&apos;ve been selected for <strong>{welcome.title}</strong> — we look forward to working with you.
                        </p>
                        <p className="text-sm text-yc-ink/80 mt-3 leading-relaxed">
                            Be sure to add all relevant details for payments, and keep your address up to date — we occasionally send out gifts.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
                            <Link to="/creator/onboarding" data-testid="welcome-update-profile" className="yc-btn-primary text-sm py-2 px-4">Update my details</Link>
                            <button data-testid="welcome-close" onClick={dismissWelcome} className="yc-btn-secondary text-sm py-2 px-4">Close</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {jobs.map((j) => {
                    const badge = STATUS_BADGE[j.application_status];
                    return (
                        <div key={j.job_id} className="yc-card p-6 flex flex-col" data-testid={`job-card-${j.job_id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <h3 className="font-heading text-xl font-bold">{j.title}</h3>
                                {badge && (
                                    <span className={`yc-badge text-[10px] shrink-0 flex items-center gap-1 ${badge.cls}`} data-testid={`job-status-${j.job_id}`}>
                                        <badge.Icon className="w-3 h-3" /> {badge.label}
                                    </span>
                                )}
                            </div>

                            {/* Brand — blurred until the job is won */}
                            <div className="mt-3">
                                {j.brand_revealed ? (
                                    <div className="inline-flex items-center gap-2 border-2 border-yc-ink rounded-full px-3 py-1 bg-yc-blue text-white text-sm font-semibold" data-testid={`job-brand-revealed-${j.job_id}`}>
                                        <Briefcase className="w-3.5 h-3.5" /> {j.brand_name}
                                    </div>
                                ) : (
                                    <div className="inline-flex items-center gap-2 border-2 border-yc-ink rounded-full px-3 py-1 bg-yc-cream text-sm" data-testid={`job-brand-blurred-${j.job_id}`}>
                                        <Lock className="w-3.5 h-3.5 shrink-0" />
                                        <span className="blur-[5px] select-none font-semibold" aria-hidden="true">Brand Name Ltd</span>
                                        <span className="text-[10px] text-yc-ink/60">revealed when you win the job</span>
                                    </div>
                                )}
                            </div>

                            {j.description && <p className="text-sm text-yc-ink/80 mt-3 leading-relaxed">{j.description}</p>}

                            {(j.example_links || []).length > 0 && (
                                <div className="mt-3 border-2 border-yc-ink rounded-2xl p-3 bg-yc-cream" data-testid={`job-examples-${j.job_id}`}>
                                    <div className="yc-overline text-[10px] flex items-center gap-1"><Film className="w-3 h-3" /> What we&apos;re looking for</div>
                                    <div className="flex flex-col gap-1 mt-1 text-xs">
                                        {j.example_links.map((l, i) => (
                                            <a key={`exl-${i}`} href={l} target="_blank" rel="noreferrer" className="text-yc-blue underline break-all">{l}</a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-1 mt-3">
                                {(j.specialisms || []).map((s) => <span key={s} className="yc-badge bg-white text-[10px]">{specLabel(s)}</span>)}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 text-sm">
                                {j.deliverables && <div><div className="yc-overline text-[10px]">Deliverables</div><div className="font-semibold mt-0.5">{j.deliverables}</div></div>}
                                {j.pay && <div><div className="yc-overline text-[10px] flex items-center gap-1"><PoundSterling className="w-3 h-3" /> Pay</div><div className="font-semibold mt-0.5">{j.pay}</div></div>}
                                {j.deadline && <div><div className="yc-overline text-[10px] flex items-center gap-1"><Calendar className="w-3 h-3" /> Deadline</div><div className="font-semibold mt-0.5">{j.deadline}</div></div>}
                            </div>

                            <div className="mt-5 pt-4 border-t-2 border-yc-ink/10">
                                {!j.applied ? (
                                    applyFor === j.job_id ? (
                                        <div className="space-y-4" data-testid={`apply-form-${j.job_id}`}>
                                            <div>
                                                <div className="yc-overline text-[10px]">Links to work you&apos;ve already done</div>
                                                <div className="space-y-2 mt-1">
                                                    {[0, 1, 2].map((i) => (
                                                        <input key={`w-${i}`} data-testid={`apply-work-link-${i}`} className="yc-input text-sm" placeholder="https://www.instagram.com/reel/…" value={links.work[i]} onChange={(e) => setLink("work", i, e.target.value)} />
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="yc-overline text-[10px]">Links showing what you propose to do</div>
                                                <div className="space-y-2 mt-1">
                                                    {[0, 1, 2].map((i) => (
                                                        <input key={`p-${i}`} data-testid={`apply-proposal-link-${i}`} className="yc-input text-sm" placeholder="https://…" value={links.proposal[i]} onChange={(e) => setLink("proposal", i, e.target.value)} />
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    data-testid={`job-apply-submit-${j.job_id}`}
                                                    disabled={applying === j.job_id}
                                                    onClick={() => apply(j)}
                                                    className="yc-btn-primary text-sm py-2.5 px-6 disabled:opacity-50"
                                                >
                                                    {applying === j.job_id ? "Applying…" : "Send application"}
                                                </button>
                                                <button data-testid={`job-apply-cancel-${j.job_id}`} onClick={() => setApplyFor("")} className="yc-btn-secondary text-sm py-2.5 px-4">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            data-testid={`job-apply-${j.job_id}`}
                                            disabled={j.status !== "open"}
                                            onClick={() => openApply(j)}
                                            className="yc-btn-primary text-sm py-2.5 px-6 disabled:opacity-50"
                                        >
                                            Apply
                                        </button>
                                    )
                                ) : (
                                    <div>
                                        <div className="text-xs text-yc-ink/60">
                                            {j.application_status === "applied" && "Your application is with the team — we'll be in touch."}
                                            {j.application_status === "approved" && "You're on this job — check your email for next steps."}
                                            {j.application_status === "rejected" && "Not this time — keep an eye out for the next brief."}
                                        </div>
                                        {((j.work_links || []).length > 0 || (j.proposal_links || []).length > 0) && (
                                            <div className="text-[11px] text-yc-ink/50 mt-2">
                                                You sent {j.work_links.length} work link{j.work_links.length === 1 ? "" : "s"} and {j.proposal_links.length} proposal link{j.proposal_links.length === 1 ? "" : "s"}.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {loaded && jobs.length === 0 && (
                <div className="yc-card p-10 text-center text-yc-ink/60" data-testid="jobs-empty">
                    <Briefcase className="w-10 h-10 mx-auto opacity-40 mb-3" />
                    No open jobs right now — new briefs land here as soon as brands book campaigns.
                </div>
            )}
        </DashboardShell>
    );
};

export default CreatorJobs;
